import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, ChevronDown, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import api from '@/lib/axios';
import { useAuthStore } from '@/store/auth.store';
import { Cie11Search, type Cie11Result } from '@/components/cie11/Cie11Search';
import { getErrorMessage } from '@/lib/errors';
import { useConfiguracionClinica, moduloAplica } from '@/hooks/useConfiguracionClinica';

type EstadoDx = 'activo' | 'resuelto' | 'cronico';

const ESTADO_CFG: Record<EstadoDx, { label: string; cls: string; dot: string }> = {
  activo:   { label: 'Activo',   cls: 'bg-green-50 text-green-700 border-green-200',   dot: 'bg-green-500' },
  cronico:  { label: 'Crónico',  cls: 'bg-purple-50 text-purple-700 border-purple-200', dot: 'bg-purple-500' },
  resuelto: { label: 'Resuelto', cls: 'bg-gray-50 text-gray-500 border-gray-200',       dot: 'bg-gray-400' },
};

const ESTADOS: EstadoDx[] = ['activo', 'cronico', 'resuelto'];

function formatDate(d: string | Date) {
  return new Date(d).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
}

interface Props {
  pacienteId: string;
  canEdit: boolean;
  noDelete?: boolean;
  triggerAdd?: number;
}

interface DxForm {
  cie11: Cie11Result | null;
  notaLibre: string;
  estado: EstadoDx;
}

const emptyForm: DxForm = { cie11: null, notaLibre: '', estado: 'activo' };

export function DiagnosticosTab({ pacienteId, canEdit, triggerAdd }: Props) {
  const { usuario } = useAuthStore();
  const qc = useQueryClient();
  const { perfilTipo, modulosHabilitados } = useConfiguracionClinica();
  const puedeEmitir = canEdit && moduloAplica('diagnosticos_emitir', perfilTipo, modulosHabilitados);

  const [showForm,    setShowForm]    = useState(false);
  useEffect(() => { if (triggerAdd && puedeEmitir) setShowForm(true); }, [triggerAdd, puedeEmitir]);
  const [form,        setForm]        = useState<DxForm>({ ...emptyForm });
  const [stateMenuId, setStateMenuId] = useState<string | null>(null);

  const { data: diagnosticos = [], isLoading } = useQuery<any[]>({
    queryKey: ['diagnosticos', pacienteId],
    queryFn: () => api.get(`/api/pacientes/${pacienteId}/diagnosticos`).then(r => r.data),
  });

  const createMutation = useMutation({
    mutationFn: (dto: any) => api.post(`/api/pacientes/${pacienteId}/diagnosticos`, dto).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['diagnosticos', pacienteId] });
      setForm({ ...emptyForm });
      setShowForm(false);
      toast.success('Diagnóstico registrado');
    },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al guardar el diagnóstico')),
  });

  // El diagnóstico como tal es inmutable (NOM-004): solo se permite transicionar
  // su estado (activo/crónico/resuelto), nunca editar o borrar el contenido clínico.
  const cambiarEstadoMutation = useMutation({
    mutationFn: ({ id, estado }: { id: string; estado: EstadoDx }) =>
      api.patch(`/api/diagnosticos/${id}/estado`, { estado }).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['diagnosticos', pacienteId] });
      setStateMenuId(null);
      toast.success('Estado actualizado');
    },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al actualizar el estado')),
  });

  function handleCreate() {
    if (!form.cie11) { toast.error('Selecciona un diagnóstico del CIE-11'); return; }
    createMutation.mutate({
      codigoCie11:    form.cie11.theCode,
      tituloCie11:    form.cie11.title,
      idEntidadCie11: form.cie11.id,
      notaLibre:      form.notaLibre || null,
      estado:         form.estado,
      medicoId:       usuario?.id,
    });
  }

  function changeEstado(id: string, estado: EstadoDx) {
    cambiarEstadoMutation.mutate({ id, estado });
  }

  // Agrupar: activos+crónicos primero, resueltos al final
  const activos   = diagnosticos.filter(d => d.estado !== 'resuelto');
  const resueltos = diagnosticos.filter(d => d.estado === 'resuelto');

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <p className="text-xs text-text-secondary">
            <span className="font-semibold text-text-primary">{activos.length}</span> activo{activos.length !== 1 ? 's' : ''}
            {resueltos.length > 0 && <span className="ml-1 text-gray-400">· {resueltos.length} resuelto{resueltos.length !== 1 ? 's' : ''}</span>}
          </p>
        </div>
        {puedeEmitir && (
          <button onClick={() => { setShowForm(s => !s); setForm({ ...emptyForm }); }}
            className="flex items-center gap-1.5 bg-primary-600 text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-primary-700 transition-colors">
            {showForm ? <X size={13} /> : <Plus size={13} />}
            {showForm ? 'Cancelar' : 'Nuevo diagnóstico'}
          </button>
        )}
      </div>

      {/* Form */}
      {showForm && (
        <div className="border border-primary-200 rounded-xl bg-primary-50/40 p-4 space-y-3">
          <p className="text-xs font-semibold text-text-secondary uppercase tracking-wide">Nuevo diagnóstico</p>

          <Cie11Search
            value={form.cie11}
            onChange={v => setForm(p => ({ ...p, cie11: v }))}
            placeholder="Buscar diagnóstico en CIE-11..."
          />

          {form.cie11 && (
            <>
              <div>
                <label className="block text-xs text-text-secondary mb-1">Nota clínica <span className="font-normal">(opcional)</span></label>
                <textarea
                  rows={2}
                  value={form.notaLibre}
                  onChange={e => setForm(p => ({ ...p, notaLibre: e.target.value }))}
                  placeholder="Observaciones adicionales del médico..."
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary-300 bg-white"
                />
              </div>

              <div className="flex items-center gap-2">
                <label className="text-xs text-text-secondary">Estado inicial:</label>
                {ESTADOS.map(e => {
                  const cfg = ESTADO_CFG[e];
                  return (
                    <button key={e} type="button"
                      onClick={() => setForm(p => ({ ...p, estado: e }))}
                      className={cn('flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border transition-all',
                        form.estado === e ? cfg.cls : 'bg-white border-gray-200 text-text-secondary hover:border-gray-300'
                      )}>
                      <span className={cn('w-1.5 h-1.5 rounded-full', form.estado === e ? cfg.dot : 'bg-gray-300')} />
                      {cfg.label}
                    </button>
                  );
                })}
              </div>

              <div className="flex justify-end">
                <button onClick={handleCreate} disabled={createMutation.isPending}
                  className="flex items-center gap-1.5 bg-primary-600 text-white px-4 py-1.5 rounded-lg text-xs font-medium hover:bg-primary-700 disabled:opacity-50 transition-colors">
                  <Check size={13} /> {createMutation.isPending ? 'Guardando...' : 'Guardar diagnóstico'}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Loading */}
      {isLoading && <p className="text-center py-8 text-sm text-text-secondary">Cargando...</p>}

      {/* Empty */}
      {!isLoading && diagnosticos.length === 0 && (
        <div className="text-center py-12 bg-gray-50 rounded-xl border border-dashed border-gray-200">
          <p className="text-sm text-text-secondary">Sin diagnósticos registrados</p>
          {puedeEmitir && (
            <button onClick={() => setShowForm(true)} className="mt-2 text-xs text-primary-600 hover:underline">
              Registrar primer diagnóstico
            </button>
          )}
        </div>
      )}

      {/* Lista activos + crónicos */}
      {activos.length > 0 && (
        <div className="space-y-2">
          {activos.map(d => (
            <DiagnosticoCard
              key={d.id}
              d={d}
              canEdit={puedeEmitir}
              stateMenuOpen={stateMenuId === d.id}
              onToggleStateMenu={() => setStateMenuId(id => id === d.id ? null : d.id)}
              onChangeEstado={estado => changeEstado(d.id, estado)}
            />
          ))}
        </div>
      )}

      {/* Lista resueltos */}
      {resueltos.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-text-secondary uppercase tracking-wide mt-4">Resueltos</p>
          {resueltos.map(d => (
            <DiagnosticoCard
              key={d.id}
              d={d}
              canEdit={puedeEmitir}
              stateMenuOpen={stateMenuId === d.id}
              onToggleStateMenu={() => setStateMenuId(id => id === d.id ? null : d.id)}
              onChangeEstado={estado => changeEstado(d.id, estado)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Card de diagnóstico ──────────────────────────────────────────────────────

function DiagnosticoCard({
  d, canEdit, stateMenuOpen, onToggleStateMenu, onChangeEstado,
}: {
  d: any; canEdit: boolean; stateMenuOpen: boolean;
  onToggleStateMenu: () => void; onChangeEstado: (e: EstadoDx) => void;
}) {
  const estado = d.estado as EstadoDx || 'activo';
  const cfg    = ESTADO_CFG[estado];
  const titulo = d.tituloCie11 || d.descripcion || 'Sin descripción';
  const codigo = d.codigoCie11 || d.cie10;

  return (
    <div className={cn('rounded-xl border p-4 transition-all', estado === 'resuelto' ? 'bg-gray-50/50 border-gray-100' : 'bg-white border-gray-100')}>
      {/* Top row */}
      <div className="flex items-start gap-3">
        {/* Estado dot */}
        <div className="flex-shrink-0 mt-1">
          <span className={cn('w-2.5 h-2.5 rounded-full block', cfg.dot)} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-0.5">
            {codigo && (
              <span className="text-[11px] font-bold text-primary-700 bg-primary-50 border border-primary-100 px-1.5 py-0.5 rounded font-mono">
                {codigo}
              </span>
            )}
            <span className={cn('text-[10px] font-semibold px-2 py-0.5 rounded-full border', cfg.cls)}>
              {cfg.label}
            </span>
          </div>

          <p className={cn('text-sm font-semibold leading-snug', estado === 'resuelto' ? 'text-text-secondary line-through' : 'text-text-primary')}>
            {titulo}
          </p>

          {d.notaLibre && (
            <p className="text-xs text-text-secondary mt-1 italic">{d.notaLibre}</p>
          )}

          {/* Médico + fecha */}
          <div className="flex items-center gap-3 mt-1.5">
            {d.medico && (
              <span className="text-[11px] text-text-secondary">{d.medico.nombre}</span>
            )}
            <span className="text-[11px] text-gray-400">{d.createdAt ? formatDate(d.createdAt) : ''}</span>
          </div>
        </div>

        {/* Actions */}
        {canEdit && (
          <div className="flex items-center gap-1 flex-shrink-0">
            {/* Cambiar estado — el diagnóstico en sí es inmutable (NOM-004) */}
            <div className="relative">
              <button onClick={onToggleStateMenu}
                className="flex items-center gap-1 text-[11px] text-text-secondary hover:text-text-primary border border-gray-200 px-2 py-1 rounded-lg transition-colors">
                <span className={cn('w-1.5 h-1.5 rounded-full', cfg.dot)} />
                {cfg.label}
                <ChevronDown size={10} />
              </button>
              {stateMenuOpen && (
                <div className="absolute right-0 top-full mt-1 bg-white rounded-xl border border-gray-100 shadow-lg z-20 overflow-hidden min-w-[130px]">
                  {ESTADOS.filter(e => e !== estado).map(e => (
                    <button key={e} onClick={() => onChangeEstado(e)}
                      className="flex items-center gap-2 w-full text-left px-3 py-2 text-xs hover:bg-gray-50 transition-colors">
                      <span className={cn('w-2 h-2 rounded-full flex-shrink-0', ESTADO_CFG[e].dot)} />
                      {ESTADO_CFG[e].label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
